import React from 'react';
import {
  Box,
  VStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Textarea,
  Divider,
  Switch
} from '@chakra-ui/react';
import { FaDatabase, FaCog, FaTools } from 'react-icons/fa';

const ConfigurationPanel = ({
  vapiConfig = {},
  setVapiConfig = () => {},
  useWebsiteContext = false,
  setUseWebsiteContext = () => {},
  websiteContextData = '',
  useBudgetConfig = () => {},
  resetVoiceSettings = () => {},
  clearFunctionHistory = () => {},
  reloadWebsiteContext = () => {}
}) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  // Safe access to vapiConfig properties with defaults
  const {
    publicKey = '',
    assistantId = '',
    model = 'gpt-4',
    voice = 'jennifer',
    voiceProvider = 'playht',
    systemPrompt = 'You are a helpful AI assistant. Keep responses concise and conversational for voice interaction.'
  } = vapiConfig;

  // Voice options
  const voiceOptions = {
    playht: ['jennifer', 'ryan', 'sarah', 'michael'],
    elevenlabs: ['rachel', 'drew', 'clyde', 'paul'],
    openai: ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']
  };

  return (
    <Card bg={cardBg}>
      <CardBody>
        <VStack spacing={4} align="stretch">
          <Heading size="md">Configuration</Heading>
          
          <FormControl>
            <FormLabel>Vapi Public Key</FormLabel>
            <Input
              type="password"
              value={publicKey}
              onChange={(e) => setVapiConfig(prev => ({...prev, publicKey: e.target.value}))}
              placeholder="Enter your Vapi public key..."
            />
            <Text fontSize="sm" color="gray.500" mt={2}>
              Get your public key from <Text as="span" color="blue.500" fontWeight="bold">dashboard.vapi.ai</Text>
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel>Website Context Integration</FormLabel>
            <HStack spacing={4} align="center">
              <Switch
                isChecked={useWebsiteContext}
                onChange={(e) => setUseWebsiteContext(e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
              <Text fontSize="sm">
                {useWebsiteContext ? 'Using website context data' : 'Using basic system prompt only'}
              </Text>
              <Button
                size="sm"
                onClick={reloadWebsiteContext}
                leftIcon={<Icon as={FaDatabase} />}
                colorScheme="blue"
                variant="outline"
              >
                Reload Context
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={2}>
              Enable to use extracted website content as context for AI responses
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel>Quick Actions</FormLabel>
            <HStack spacing={2} wrap="wrap">
              <Button 
                size="sm" 
                onClick={useBudgetConfig}
                colorScheme="green"
                variant="outline"
              >
                💰 Budget Mode
              </Button>
              <Button 
                size="sm" 
                onClick={() => setVapiConfig(prev => ({...prev, publicKey: ''}))}
                colorScheme="gray"
                variant="outline"
              >
                Clear Key
              </Button>
              <Button 
                size="sm" 
                onClick={resetVoiceSettings}
                colorScheme="purple"
                variant="outline"
                leftIcon={<Icon as={FaCog} />}
              >
                Reset Voice Settings
              </Button>
              <Button 
                size="sm" 
                onClick={clearFunctionHistory}
                colorScheme="orange"
                variant="outline"
                leftIcon={<Icon as={FaTools} />}
              >
                Clear Function History
              </Button>
            </HStack>
          </FormControl>

          <Divider />

          <FormControl>
            <FormLabel>Assistant ID (Optional)</FormLabel>
            <Input
              value={assistantId}
              onChange={(e) => setVapiConfig(prev => ({...prev, assistantId: e.target.value}))}
              placeholder="Optional: Use existing assistant ID..."
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              Leave empty to create dynamic assistant with function calling enabled
            </Text>
          </FormControl>

          <HStack spacing={4}>
            <FormControl>
              <FormLabel>AI Model</FormLabel>
              <Select
                value={model}
                onChange={(e) => setVapiConfig(prev => ({...prev, model: e.target.value}))}
              >
                <option value="gpt-4">GPT-4 (Best Quality)</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster)</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Voice Provider</FormLabel>
              <Select
                value={voiceProvider}
                onChange={(e) => setVapiConfig(prev => ({...prev, voiceProvider: e.target.value}))}
              >
                <option value="playht">PlayHT (Recommended)</option>
                <option value="elevenlabs">ElevenLabs (Premium)</option>
                <option value="openai">OpenAI (Fast)</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Voice</FormLabel>
              <Select
                value={voice}
                onChange={(e) => setVapiConfig(prev => ({...prev, voice: e.target.value}))}
              >
                {voiceOptions[voiceProvider]?.map(voiceOption => (
                  <option key={voiceOption} value={voiceOption}>{voiceOption}</option>
                ))}
              </Select>
            </FormControl>
          </HStack>

          <FormControl>
            <FormLabel>Base System Prompt</FormLabel>
            <Textarea
              value={systemPrompt}
              onChange={(e) => setVapiConfig(prev => ({...prev, systemPrompt: e.target.value}))}
              placeholder="Enter base system prompt for the AI assistant..."
              rows={3}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {useWebsiteContext ? 'This will be combined with website context data and function calling capabilities' : 'This will be used as the main system prompt'}
            </Text>
          </FormControl>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ConfigurationPanel;